import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

/* ─── Helpers (same as CustomerBookingDetail) ─────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d); }
};

const rupee = (v) =>
  v != null && v !== '' && !isNaN(v) && parseFloat(v) !== 0
    ? `₹ ${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : null;

const statusMeta = (s) => {
  switch (s?.toUpperCase()) {
    case 'CANCELLED':
    case 'AUTO_CANCELLED': return { cls: 'bg-red-100 text-red-700 border-red-200',   dot: 'bg-red-500'   };
    case 'IN_PROGRESS':    return { cls: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
    default:               return { cls: 'bg-gray-100 text-gray-600 border-gray-200',   dot: 'bg-gray-400'  };
  }
};

const InfoRow = ({ label, value, mono = false, highlight = false }) => {
  if (value == null || value === '' || value === 'null' || value === 'N/A') return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 uppercase tracking-wide w-44 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm flex-1 break-words ${mono ? 'font-mono' : ''} ${highlight ? 'text-blue-600 font-semibold' : 'text-gray-800 font-medium'}`}>
        {value}
      </span>
    </div>
  );
};

const SectionCard = ({ title, icon, accent = 'blue', children }) => {
  const map = {
    blue:   'border-blue-500   bg-blue-50/60   text-blue-700',
    teal:   'border-teal-500   bg-teal-50/60   text-teal-700',
    violet: 'border-violet-500 bg-violet-50/60 text-violet-700',
    amber:  'border-amber-500  bg-amber-50/60  text-amber-700',
    rose:   'border-rose-500   bg-rose-50/60   text-rose-700',
    slate:  'border-slate-400  bg-slate-50/60  text-slate-700',
    green:  'border-green-500  bg-green-50/60  text-green-700',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
      <div className={`flex items-center gap-2.5 px-5 py-3.5 border-l-4 ${map[accent] || map.blue}`}>
        <i className={`${icon} text-sm`}></i>
        <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
};

const PriceRow = ({ label, value, total = false }) => {
  if (value == null) return null;
  return (
    <div className={`flex justify-between items-center py-2.5 ${total ? 'border-t-2 border-gray-200 mt-1 pt-3' : 'border-b border-gray-50'}`}>
      <span className={`text-sm ${total ? 'font-bold text-gray-900' : 'text-gray-500'}`}>{label}</span>
      <span className={`font-semibold ${total ? 'text-lg text-blue-600' : 'text-sm text-gray-800'}`}>{value}</span>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const CancellationDetail = () => {
  const { cancellationId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showTicket, setShowTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ queryType: 'CANCELLATION', query: '' });

  useEffect(() => { if (cancellationId) load(); }, [cancellationId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await agentApiService.getCancellationDetail(cancellationId);
      if (res.success) setDetail(res.cancellationDetail);
      else toast.error(res.message || 'Failed to load');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const doTicket = async (e) => {
    e.preventDefault();
    try {
      const r = await agentApiService.createBookingSupportTicket(detail?.bookingId, ticketForm);
      if (r.success) { toast.success('Ticket submitted'); setShowTicket(false); setTicketForm({ queryType: 'CANCELLATION', query: '' }); }
      else toast.error(r.message);
    } catch (e) { toast.error(e.message); }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-rose-500 border-t-transparent"></div>
        <p className="text-sm text-gray-400">Loading cancellation details…</p>
      </div>
    </div>
  );

  /* ── Not found ── */
  if (!detail) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-exclamation-triangle text-2xl text-red-500"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Cancellation Not Found</h2>
        <p className="text-sm text-gray-500 mb-5">This record doesn't exist or you don't have access.</p>
        <button onClick={() => navigate('/agent/dashboard/booking-cancellation')}
          className="bg-rose-600 text-white px-5 py-2 rounded-xl text-sm hover:bg-rose-700">
          ← Back to Cancellations
        </button>
      </div>
    </div>
  );

  const sm = statusMeta(detail.status);
  const passengerName = [detail.passengerFirstName, detail.passengerLastName].filter(Boolean).join(' ') || null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Top Bar ── */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agent/dashboard/booking-cancellation')}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-rose-600 hover:border-rose-300 shadow-sm transition">
              <i className="fas fa-arrow-left text-sm"></i>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Cancellation Details</h1>
              <p className="text-xs text-gray-400 mt-0.5 font-mono">{cancellationId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {detail.status && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${sm.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`}></span>
                {detail.status}
              </span>
            )}
            <button onClick={() => setShowTicket(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition">
              <i className="fas fa-headset text-xs"></i>Support Ticket
            </button>
          </div>
        </div>

        {/* ── Hero Banner ── */}
        <div className="bg-gradient-to-r from-rose-600 to-red-700 rounded-2xl p-6 mb-5 text-white shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              {detail.hotelName && <h2 className="text-xl font-bold mb-1">{detail.hotelName}</h2>}
              {detail.city && (
                <p className="text-rose-200 text-sm flex items-center gap-1.5">
                  <i className="fas fa-map-marker-alt text-xs"></i>{detail.city}
                </p>
              )}
              {/* Cancellation ID prominently shown */}
              <div className="mt-3 inline-flex items-center gap-2 bg-white/20 rounded-xl px-3 py-1.5">
                <i className="fas fa-ban text-xs text-rose-200"></i>
                <span className="text-xs text-rose-100 uppercase tracking-wide">Cancellation ID</span>
                <span className="text-white font-mono font-bold text-sm">{detail.cancellationId || cancellationId}</span>
              </div>
            </div>
            <div className="flex gap-6 sm:text-right">
              {detail.checkInDate && (
                <div>
                  <p className="text-rose-300 text-xs uppercase tracking-wide mb-0.5">Check-in</p>
                  <p className="text-white font-semibold text-sm">{fmtDate(detail.checkInDate)}</p>
                </div>
              )}
              {detail.checkOutDate && (
                <div>
                  <p className="text-rose-300 text-xs uppercase tracking-wide mb-0.5">Check-out</p>
                  <p className="text-white font-semibold text-sm">{fmtDate(detail.checkOutDate)}</p>
                </div>
              )}
              {detail.cancellationDate && (
                <div>
                  <p className="text-rose-300 text-xs uppercase tracking-wide mb-0.5">Cancelled On</p>
                  <p className="text-white font-semibold text-sm">{fmtDate(detail.cancellationDate)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ════ LEFT (2 cols) ════ */}
          <div className="lg:col-span-2">

            {/* ── Cancellation Overview ── */}
            <SectionCard title="Cancellation Overview" icon="fas fa-ban" accent="rose">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <div>
                  <InfoRow label="Cancellation ID" value={detail.cancellationId || cancellationId} mono highlight />
                  <InfoRow label="Booking ID"       value={detail.bookingId} mono />
                  <InfoRow label="Status"           value={detail.status} />
                </div>
                <div>
                  <InfoRow label="Cancelled On"    value={fmtDate(detail.cancellationDate)} />
                  <InfoRow label="Booking Date"    value={fmtDate(detail.bookingDate)} />
                  <InfoRow label="Confirmation No" value={detail.confirmationNo} mono />
                  <InfoRow label="Hotel Conf. No"  value={detail.hotelConfirmationNo} mono />
                </div>
              </div>
            </SectionCard>

            {/* ── Hotel & Stay Info ── */}
            <SectionCard title="Hotel & Stay Info" icon="fas fa-hotel" accent="teal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <div>
                  <InfoRow label="Hotel Name"  value={detail.hotelName} />
                  <InfoRow label="City"        value={detail.city} />
                  <InfoRow label="Address"     value={detail.address} />
                  <InfoRow label="Provider"    value={detail.providerName} />
                </div>
                <div>
                  <InfoRow label="Check-in"    value={fmtDate(detail.checkInDate)} />
                  <InfoRow label="Check-out"   value={fmtDate(detail.checkOutDate)} />
                  <InfoRow label="Room Type"   value={detail.roomType} />
                </div>
              </div>
            </SectionCard>

            {/* ── Guest Info ── */}
            {(passengerName || detail.passengerEmail || detail.passengerPhone || detail.phoneNumber) && (
              <SectionCard title="Guest Information" icon="fas fa-user" accent="violet">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <div>
                    <InfoRow label="Guest Name"  value={passengerName} />
                    <InfoRow label="Email"       value={detail.passengerEmail} />
                  </div>
                  <div>
                    <InfoRow label="Phone"       value={detail.passengerPhone || detail.phoneNumber} />
                    <InfoRow label="PAN"         value={detail.passengerPANCardNumber} mono />
                    <InfoRow label="Age"         value={detail.age != null ? String(detail.age) : null} />
                  </div>
                </div>
              </SectionCard>
            )}

          </div>

          {/* ════ RIGHT Sidebar ════ */}
          <div className="space-y-5">

            {/* ── Price Summary ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-l-4 border-purple-500 bg-purple-50/60 text-purple-700">
                <i className="fas fa-calculator text-sm"></i>
                <h3 className="text-sm font-semibold tracking-wide">Price Summary</h3>
              </div>
              <div className="px-5 py-4">
                <PriceRow label="Room Price"   value={rupee(detail.roomPrice)} />
                <PriceRow label="Platform Fee" value={rupee(detail.platformFee)} />
                <PriceRow label="GST"          value={rupee(detail.gstAmount)} />
                <PriceRow label="Net Amount"   value={rupee(detail.netAmount)} />
                <PriceRow label="Total Price"  value={rupee(detail.totalPrice)} total />
              </div>
            </div>

            {/* ── Cancellation Alert ── */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="fas fa-ban text-red-600 text-xs"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-800">Booking Cancelled</p>
                  {detail.cancellationDate && (
                    <p className="text-xs text-red-600 mt-0.5">
                      Cancelled on {fmtDate(detail.cancellationDate)}
                    </p>
                  )}
                  <p className="text-xs text-red-500 mt-1 font-mono">{detail.cancellationId || cancellationId}</p>
                </div>
              </div>
            </div>

            {/* ── Refund Info ── */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                  <i className="fas fa-undo text-amber-600 text-xs"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Refund Processing</p>
                  <p className="text-xs text-amber-600 mt-0.5">Refund will be processed within 7–10 business days to the original payment method.</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ════ Support Ticket Modal ════ */}
        {showTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl relative">
              <button onClick={() => setShowTicket(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <i className="fas fa-headset text-blue-600"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900">Raise Support Ticket</h3>
              </div>
              <form onSubmit={doTicket} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Cancellation ID</label>
                  <input type="text" value={cancellationId} disabled
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm font-mono text-gray-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Query Type</label>
                  <select value={ticketForm.queryType} onChange={e => setTicketForm({ ...ticketForm, queryType: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="CANCELLATION">CANCELLATION</option>
                    <option value="BOOKING">BOOKING</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Your Query</label>
                  <textarea value={ticketForm.query} onChange={e => setTicketForm({ ...ticketForm, query: e.target.value })}
                    rows={4} required placeholder="Describe your issue…"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" />
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="submit"
                    className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 transition">
                    Submit Ticket
                  </button>
                  <button type="button" onClick={() => setShowTicket(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CancellationDetail;
